import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from './streamApi';

// Auto-update status of this box (prvsk-updater). The box's own API answers
// /update/status — but while an update restarts it, that API is down. The box
// reports every state change to the router, so in that window we ask the router
// (/router/updates/client-status) instead; that's what lets the "System is being
// updated" screen stay up instead of the page just failing.
//
// Polls every 60 s, every 10 s while an install is in flight.

export const UPDATE_STATUS_CHANGED = 'prvsk:update-status-changed';
const BUSY = new Set(['installing', 'health_check', 'rolling_back']);
const SLOW_MS = 60000;
const FAST_MS = 10000;

export function isBusy(st) { return !!st && BUSY.has(st.state); }

async function fromRouter() {
  try {
    const r = await apiFetch('/router/updates/client-status');
    if (!r.ok) return null;
    const d = await r.json();
    // A client with several boxes: the busiest one is what the user needs to see.
    const boxes = d.boxes || [];
    const b = boxes.find(x => BUSY.has(x.state)) || boxes[0];
    if (!b) return null;
    return { state: b.state, current_build: b.current_build, current_version: b.current_version,
             last_result: b.last_result, via: 'router' };
  } catch { return null; }
}

export function useUpdateStatus() {
  const [status, setStatus] = useState(null);
  const lastRef = useRef(null);

  const load = useCallback(async () => {
    let st = null;
    try {
      const r = await apiFetch('/update/status');
      if (r.ok) st = await r.json();
      else if (r.status >= 500 || r.status === 0) st = await fromRouter();
      // 404 = a box from before auto-update — show nothing
    } catch {
      st = await fromRouter();     // box API unreachable — it may be mid-update
    }
    // While the box is restarting, keep showing "updating" rather than flicker
    // to nothing between the last box answer and the first router answer.
    if (!st && isBusy(lastRef.current)) st = lastRef.current;
    lastRef.current = st;
    setStatus(st);
  }, []);

  useEffect(() => {
    let t;
    const tick = async () => {
      await load();
      t = setTimeout(tick, isBusy(lastRef.current) ? FAST_MS : SLOW_MS);
    };
    tick();
    window.addEventListener(UPDATE_STATUS_CHANGED, load);
    return () => { clearTimeout(t); window.removeEventListener(UPDATE_STATUS_CHANGED, load); };
  }, [load]);

  return [status, load];
}

export async function requestUpdate(action, build) {
  const r = await apiFetch(`/update/${action}`, {
    method: 'POST', json: build ? { build } : {},
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { msg = (await r.json()).detail || msg; } catch { /* keep HTTP code */ }
    throw new Error(msg);
  }
  window.dispatchEvent(new Event(UPDATE_STATUS_CHANGED));
  return r.json();
}

export function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
}
